import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import {
  ConnectedAccountsPage,
  type ConnectedAccountsNotice,
} from "../../../components/dashboard/connected-accounts";
import { useOneTimePageAnimation } from "@/components/motion";
import {
  connectedAccountsQueryKey,
  connectedAccountsQueryOptions,
} from "@/features/connected-accounts/queries";
import {
  addWarmupConnectionAction,
  disableWarmupAction,
  disconnectConnectedAccountAction,
  enableWarmupAction,
  removeWarmupConnectionAction,
  startConnectedAccountAuthorizationAction,
} from "@/features/connected-accounts/server-fns";
import type { ConnectedAccountView } from "@/features/connected-accounts/view-model";
import { capturePostHogEvent } from "@/lib/posthog-client";

// What the provider's redirect lands here with (the backend's CONNECTED_ACCOUNTS_RETURN_URL):
// the new or regranted account's id, or the refusal's code.
type ConnectedAccountsSearch = {
  provider?: string;
  connected_account_id?: string;
  error?: string;
  grant?: string;
};

const ERROR_MESSAGES: Record<string, string> = {
  connected_account_authorization_invalid:
    "The Google sign-in did not complete (the consent was refused, expired or already used). Start again.",
  connected_account_provider_refused:
    "Google refused the authorization. Make sure the account is one of the allowed test users and try again.",
  connected_account_provider_unavailable:
    "Google could not be reached to complete the authorization. Try again in a moment.",
  connected_accounts_not_configured: "Google connections are not configured on this deployment.",
  auth_account_frozen: "This account is frozen; connecting a mailbox is not possible right now.",
};

function parseSearch(search: Record<string, unknown>): ConnectedAccountsSearch {
  const text = (value: unknown) => (typeof value === "string" && value ? value : undefined);
  return {
    provider: text(search.provider),
    connected_account_id: text(search.connected_account_id),
    error: text(search.error),
    grant: text(search.grant),
  };
}

export const Route = createFileRoute("/dashboard/connected-accounts/")({
  validateSearch: parseSearch,
  head: () => ({
    meta: [{ title: "Connected accounts - Bavimail" }],
  }),
  staleTime: 15_000,
  preloadStaleTime: 15_000,
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(connectedAccountsQueryOptions());
  },
  component: ConnectedAccountsRoute,
});

function ConnectedAccountsRoute() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const { data: view } = useSuspenseQuery({
    ...connectedAccountsQueryOptions(),
    // A pass is running, a fresh connection waits for its first one, or a failed one is about to
    // be retried: poll until the account settles.
    refetchInterval: (query) => {
      const current = query.state.data;
      if (!current) return false;
      const now = Date.now();
      const moving = current.accounts.some((account) => {
        if (account.status !== "active") return false;
        if (account.syncingUntil !== null && new Date(account.syncingUntil).getTime() > now) {
          return true;
        }
        if (account.lastSyncError) {
          return account.syncRetryAt === null || new Date(account.syncRetryAt).getTime() <= now;
        }
        return !account.backfillCompletedAt;
      });
      return moving ? 5_000 : false;
    },
  });
  const animationStage = useOneTimePageAnimation("dashboard-connected-accounts");

  const notice = useMemo<ConnectedAccountsNotice | null>(() => {
    if (search.error) {
      return {
        kind: "error",
        message: ERROR_MESSAGES[search.error] ?? `The connection failed (${search.error}).`,
      };
    }
    if (search.connected_account_id) {
      const account = view.accounts.find((item) => item.id === search.connected_account_id);
      const address = account ? account.emailAddress : "the account";
      return search.grant === "warmup"
        ? {
            kind: "granted",
            message: `Warmup access granted for ${address}. Start the warmup below.`,
          }
        : {
            kind: "connected",
            message: `${address} is connected. Its last 30 days of mail are being pulled in; new mail follows as it arrives.`,
          };
    }
    return null;
  }, [search.connected_account_id, search.error, search.grant, view.accounts]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: connectedAccountsQueryKey() });

  const authorize = useMutation({
    mutationFn: async (input: { grants?: Array<"warmup">; loginHint?: string | null }) =>
      await startConnectedAccountAuthorizationAction({
        data: { provider: "google", loginHint: input.loginHint ?? null, grants: input.grants },
      }),
    onSuccess: (result, input) => {
      capturePostHogEvent(
        input.grants?.length
          ? "connected_account_grant_started"
          : "connected_account_connect_started",
      );
      window.location.assign(result.authorizationUrl);
    },
  });

  const disconnect = useMutation({
    mutationFn: async (account: ConnectedAccountView) =>
      await disconnectConnectedAccountAction({ data: { connectedAccountId: account.id } }),
    onSuccess: async () => {
      capturePostHogEvent("connected_account_disconnected");
      await invalidate();
    },
  });

  const enableWarmup = useMutation({
    mutationFn: async (input: {
      account: ConnectedAccountView;
      providerId: "warmy" | "mailivery" | "bavimail";
      dailyVolume: number | null;
    }) =>
      await enableWarmupAction({
        data: {
          inboxId: input.account.inboxId,
          providerId: input.providerId,
          dailyVolume: input.dailyVolume,
        },
      }),
    onSuccess: async (result) => {
      if (result.kind === "enabled") {
        capturePostHogEvent("connected_account_warmup_enabled");
        await invalidate();
      }
    },
  });

  const disableWarmup = useMutation({
    mutationFn: async (account: ConnectedAccountView) => {
      if (!account.warmup) return;
      await disableWarmupAction({ data: { warmupId: account.warmup.id } });
    },
    onSuccess: async () => {
      capturePostHogEvent("connected_account_warmup_disabled");
      await invalidate();
    },
  });

  const addWarmupConnection = useMutation({
    mutationFn: async (input: {
      providerId: "warmy" | "mailivery";
      credentials: Record<string, string>;
    }) => await addWarmupConnectionAction({ data: input }),
    onSuccess: async (_result, input) => {
      capturePostHogEvent("warmup_connection_added", { provider: input.providerId });
      await invalidate();
    },
  });

  const removeWarmupConnection = useMutation({
    mutationFn: async (connectionId: string) =>
      await removeWarmupConnectionAction({ data: { connectionId } }),
    onSuccess: async () => {
      capturePostHogEvent("warmup_connection_removed");
      await invalidate();
    },
  });

  return (
    <ConnectedAccountsPage
      view={view}
      notice={notice}
      animationStage={animationStage}
      onConnect={async () => {
        await authorize.mutateAsync({});
      }}
      onDisconnect={async (account) => {
        await disconnect.mutateAsync(account);
      }}
      onGrantWarmup={async (account) => {
        await authorize.mutateAsync({ grants: ["warmup"], loginHint: account.emailAddress });
      }}
      onEnableWarmup={async (input) => {
        const result = await enableWarmup.mutateAsync(input);
        if (result.kind === "authorization_required") {
          await authorize.mutateAsync({
            grants: ["warmup"],
            loginHint: input.account.emailAddress,
          });
        }
        return result.kind;
      }}
      onDisableWarmup={async (account) => {
        await disableWarmup.mutateAsync(account);
        await navigate({ to: "/dashboard/connected-accounts", search: {}, replace: true });
      }}
      onAddWarmupConnection={async (input) => {
        await addWarmupConnection.mutateAsync(input);
      }}
      onRemoveWarmupConnection={async (connectionId) => {
        await removeWarmupConnection.mutateAsync(connectionId);
      }}
    />
  );
}
